import { supabase } from '@/integrations/supabase/client';

export interface GenreRecommendation {
  id: string;
  name: string;
  slug: string;
  category: string;
  sequence_count: number;
  total_downloads: number;
  average_rating: number;
  featured: boolean;
  reason: string;
  confidence: number;
  similarity_score?: number;
}

export interface RecommendationContext {
  currentGenreIds: string[];
  currentGenreCategories: string[];
  userHistory?: string[];
  searchQuery?: string;
  limit?: number;
}

export class GenreRecommendationService {
  /**
   * Get comprehensive genre recommendations based on current context
   */
  static async getRecommendations(context: RecommendationContext): Promise<{
    popular: GenreRecommendation[];
    related: GenreRecommendation[];
    featured: GenreRecommendation[];
    trending: GenreRecommendation[];
    crossCategory: GenreRecommendation[];
  }> {
    const {
      currentGenreIds = [],
      currentGenreCategories = [],
      userHistory = [],
      searchQuery = '',
      limit = 10
    } = context;

    try {
      // Get all active genres with statistics
      const { data: genres, error } = await supabase
        .from('genre_stats')
        .select('*')
        .eq('is_active', true)
        .order('sequence_count', { ascending: false });

      if (error) throw error;

      if (!genres) return {
        popular: [],
        related: [],
        featured: [],
        trending: [],
        crossCategory: []
      };

      // Calculate recommendations
      const recommendations = {
        popular: this.getPopularRecommendations(genres, limit),
        related: this.getRelatedRecommendations(genres, currentGenreIds, currentGenreCategories, limit),
        featured: this.getFeaturedRecommendations(genres, limit),
        trending: this.getTrendingRecommendations(genres, limit),
        crossCategory: this.getCrossCategoryRecommendations(genres, currentGenreCategories, limit)
      };

      return recommendations;
    } catch (error) {
      console.error('Error getting genre recommendations:', error);
      return {
        popular: [],
        related: [],
        featured: [],
        trending: [],
        crossCategory: []
      };
    }
  }

  /**
   * Get popular genres based on sequence count and downloads
   */
  private static getPopularRecommendations(genres: any[], limit: number): GenreRecommendation[] {
    return genres
      .filter(genre => genre.sequence_count >= 5)
      .sort((a, b) => {
        // Weighted scoring: 60% sequence count, 40% downloads
        const scoreA = (a.sequence_count * 0.6) + (a.total_downloads * 0.4);
        const scoreB = (b.sequence_count * 0.6) + (b.total_downloads * 0.4);
        return scoreB - scoreA;
      })
      .slice(0, limit)
      .map(genre => ({
        id: genre.id,
        name: genre.name,
        slug: genre.slug,
        category: genre.category,
        sequence_count: genre.sequence_count,
        total_downloads: genre.total_downloads,
        average_rating: genre.average_rating,
        featured: genre.featured,
        reason: 'Popular genre with many sequences',
        confidence: this.calculatePopularityConfidence(genre.sequence_count, genre.total_downloads)
      }));
  }

  /**
   * Get related genres based on current selections
   */
  private static getRelatedRecommendations(
    genres: any[], 
    currentGenreIds: string[], 
    currentGenreCategories: string[], 
    limit: number
  ): GenreRecommendation[] {
    if (currentGenreIds.length === 0 && currentGenreCategories.length === 0) {
      return [];
    }

    const relatedGenres = genres
      .filter(genre => !currentGenreIds.includes(genre.id))
      .map(genre => {
        let similarityScore = 0;
        let reason = '';

        // Category similarity
        if (currentGenreCategories.includes(genre.category)) {
          similarityScore += 0.4;
          reason = `Similar to ${genre.category} genres`;
        }

        // Popularity similarity (similar sequence count ranges)
        const currentGenres = genres.filter(g => currentGenreIds.includes(g.id));
        if (currentGenres.length > 0) {
          const avgCurrentCount = currentGenres.reduce((sum, g) => sum + g.sequence_count, 0) / currentGenres.length;
          const countDiff = Math.abs(genre.sequence_count - avgCurrentCount);
          const maxCount = Math.max(...genres.map(g => g.sequence_count));
          const countSimilarity = 1 - (countDiff / maxCount);
          similarityScore += countSimilarity * 0.3;

          if (countSimilarity > 0.7) {
            reason = `Similar popularity to your selections`;
          }
        }

        // Rating similarity
        if (currentGenres.length > 0) {
          const avgCurrentRating = currentGenres.reduce((sum, g) => sum + g.average_rating, 0) / currentGenres.length;
          const ratingDiff = Math.abs(genre.average_rating - avgCurrentRating);
          const ratingSimilarity = 1 - (ratingDiff / 5);
          similarityScore += ratingSimilarity * 0.2;

          if (ratingSimilarity > 0.8) {
            reason = `Similar quality to your selections`;
          }
        }

        // Featured bonus
        if (genre.featured) {
          similarityScore += 0.1;
          reason = 'Featured genre similar to your selections';
        }

        return {
          id: genre.id,
          name: genre.name,
          slug: genre.slug,
          category: genre.category,
          sequence_count: genre.sequence_count,
          total_downloads: genre.total_downloads,
          average_rating: genre.average_rating,
          featured: genre.featured,
          reason: reason || 'Related to your selections',
          confidence: Math.min(similarityScore, 1),
          similarity_score: similarityScore
        };
      })
      .filter(genre => genre.confidence > 0.2)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, limit);

    return relatedGenres;
  }

  /**
   * Get featured genres
   */
  private static getFeaturedRecommendations(genres: any[], limit: number): GenreRecommendation[] {
    return genres
      .filter(genre => genre.featured)
      .sort((a, b) => b.sequence_count - a.sequence_count)
      .slice(0, limit)
      .map(genre => ({
        id: genre.id,
        name: genre.name,
        slug: genre.slug,
        category: genre.category,
        sequence_count: genre.sequence_count,
        total_downloads: genre.total_downloads,
        average_rating: genre.average_rating,
        featured: genre.featured,
        reason: 'Featured genre',
        confidence: 0.9
      }));
  }

  /**
   * Get trending genres (recently popular or growing)
   */
  private static getTrendingRecommendations(genres: any[], limit: number): GenreRecommendation[] {
    // For now, we'll use a combination of high ratings and moderate popularity
    // In a real implementation, you'd track growth over time
    return genres
      .filter(genre => 
        genre.average_rating >= 4.0 && 
        genre.sequence_count >= 10 && 
        genre.sequence_count <= 50
      )
      .sort((a, b) => {
        // Weighted scoring: 50% rating, 30% downloads, 20% sequence count
        const scoreA = (a.average_rating * 0.5) + (a.total_downloads * 0.3) + (a.sequence_count * 0.2);
        const scoreB = (b.average_rating * 0.5) + (b.total_downloads * 0.3) + (b.sequence_count * 0.2);
        return scoreB - scoreA;
      })
      .slice(0, limit)
      .map(genre => ({
        id: genre.id,
        name: genre.name,
        slug: genre.slug,
        category: genre.category,
        sequence_count: genre.sequence_count,
        total_downloads: genre.total_downloads,
        average_rating: genre.average_rating,
        featured: genre.featured,
        reason: 'Trending genre with high ratings',
        confidence: this.calculateTrendingConfidence(genre.average_rating, genre.sequence_count)
      }));
  }

  /**
   * Get cross-category recommendations
   */
  private static getCrossCategoryRecommendations(
    genres: any[], 
    currentCategories: string[], 
    limit: number
  ): GenreRecommendation[] {
    if (currentCategories.length === 0) {
      return [];
    }

    return genres
      .filter(genre => !currentCategories.includes(genre.category))
      .filter(genre => genre.sequence_count >= 5)
      .sort((a, b) => b.sequence_count - a.sequence_count)
      .slice(0, limit)
      .map(genre => ({
        id: genre.id,
        name: genre.name,
        slug: genre.slug,
        category: genre.category,
        sequence_count: genre.sequence_count,
        total_downloads: genre.total_downloads,
        average_rating: genre.average_rating,
        featured: genre.featured,
        reason: `Explore ${genre.category} category`,
        confidence: 0.7
      }));
  }

  /**
   * Calculate popularity confidence score
   */
  private static calculatePopularityConfidence(sequenceCount: number, totalDownloads: number): number {
    const maxSequences = 100; // Normalize to max expected sequences
    const maxDownloads = 10000; // Normalize to max expected downloads
    
    const sequenceScore = Math.min(sequenceCount / maxSequences, 1);
    const downloadScore = Math.min(totalDownloads / maxDownloads, 1);
    
    return (sequenceScore * 0.6) + (downloadScore * 0.4);
  }

  /**
   * Calculate trending confidence score
   */
  private static calculateTrendingConfidence(averageRating: number, sequenceCount: number): number {
    const ratingScore = averageRating / 5;
    const countScore = Math.min(sequenceCount / 50, 1);
    
    return (ratingScore * 0.7) + (countScore * 0.3);
  }

  /**
   * Get personalized recommendations based on user history
   */
  static async getPersonalizedRecommendations(
    userId: string, 
    context: RecommendationContext
  ): Promise<GenreRecommendation[]> {
    try {
      // Get user's genre interaction history
      const { data: userHistory, error } = await supabase
        .from('user_genre_interactions')
        .select('genre_id, interaction_type, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Get all genres
      const { data: genres, error: genresError } = await supabase
        .from('genre_stats')
        .select('*')
        .eq('is_active', true);

      if (genresError) throw genresError;

      if (!genres || !userHistory) return [];

      // Calculate personalized scores
      const personalizedGenres = genres
        .filter(genre => !context.currentGenreIds.includes(genre.id))
        .map(genre => {
          const userInteractions = userHistory.filter(h => h.genre_id === genre.id);
          let personalScore = 0;

          // Weight recent interactions more heavily
          userInteractions.forEach(interaction => {
            const daysAgo = (Date.now() - new Date(interaction.created_at).getTime()) / (1000 * 60 * 60 * 24);
            const timeWeight = Math.max(0.1, 1 - (daysAgo / 365)); // Decay over a year
            
            switch (interaction.interaction_type) {
              case 'view':
                personalScore += 0.1 * timeWeight;
                break;
              case 'download':
                personalScore += 0.5 * timeWeight;
                break;
              case 'favorite':
                personalScore += 0.8 * timeWeight;
                break;
              case 'search':
                personalScore += 0.2 * timeWeight;
                break;
            }
          });

          return {
            id: genre.id,
            name: genre.name,
            slug: genre.slug,
            category: genre.category,
            sequence_count: genre.sequence_count,
            total_downloads: genre.total_downloads,
            average_rating: genre.average_rating,
            featured: genre.featured,
            reason: 'Based on your history',
            confidence: Math.min(personalScore, 1)
          };
        })
        .filter(genre => genre.confidence > 0.1)
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, context.limit || 10);

      return personalizedGenres;
    } catch (error) {
      console.error('Error getting personalized recommendations:', error);
      return [];
    }
  }

  /**
   * Get genre recommendations for a specific search query
   */
  static async getSearchBasedRecommendations(
    searchQuery: string, 
    limit: number = 10
  ): Promise<GenreRecommendation[]> {
    try {
      const { data: genres, error } = await supabase
        .from('genre_stats')
        .select('*')
        .eq('is_active', true)
        .or(`name.ilike.%${searchQuery}%,category.ilike.%${searchQuery}%`);

      if (error) throw error;

      if (!genres) return [];

      return genres
        .sort((a, b) => b.sequence_count - a.sequence_count)
        .slice(0, limit)
        .map(genre => ({
          id: genre.id,
          name: genre.name,
          slug: genre.slug,
          category: genre.category,
          sequence_count: genre.sequence_count,
          total_downloads: genre.total_downloads,
          average_rating: genre.average_rating,
          featured: genre.featured,
          reason: `Matches your search: "${searchQuery}"`,
          confidence: 0.8
        }));
    } catch (error) {
      console.error('Error getting search-based recommendations:', error);
      return [];
    }
  }
} 